import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import Index from "./pages/Index";

// Lazy load all non-home pages
const Servizi = lazy(() => import("./pages/Servizi"));
const ServiziB2B = lazy(() => import("./pages/ServiziB2B"));
const ServiziB2C = lazy(() => import("./pages/ServiziB2C"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Cookie = lazy(() => import("./pages/Cookie"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Solution pages - lazy
const AutomazioniAI = lazy(() => import("./pages/soluzioni/AutomazioniAI"));
const CRM = lazy(() => import("./pages/soluzioni/CRM"));
const EmailMarketing = lazy(() => import("./pages/soluzioni/EmailMarketing"));
const DatabaseLeads = lazy(() => import("./pages/soluzioni/DatabaseLeads"));
const AdsB2B = lazy(() => import("./pages/soluzioni/AdsB2B"));
const SEO = lazy(() => import("./pages/soluzioni/SEO"));
const SviluppoWeb = lazy(() => import("./pages/soluzioni/SviluppoWeb"));
const Consulenza = lazy(() => import("./pages/soluzioni/Consulenza"));
const SocialB2B = lazy(() => import("./pages/soluzioni/SocialB2B"));
const Gestionali = lazy(() => import("./pages/soluzioni/Gestionali"));
const Integrazioni = lazy(() => import("./pages/soluzioni/Integrazioni"));
const SoftwareCustom = lazy(() => import("./pages/soluzioni/SoftwareCustom"));
const AssistenteWhatsApp = lazy(() => import("./pages/soluzioni/AssistenteWhatsApp"));
const SegretariaAI = lazy(() => import("./pages/soluzioni/SegretariaAI"));
const PrenotazioniOnline = lazy(() => import("./pages/soluzioni/PrenotazioniOnline"));
const SocialMediaLocale = lazy(() => import("./pages/soluzioni/SocialMediaLocale"));
const AdsLocale = lazy(() => import("./pages/soluzioni/AdsLocale"));
const GoogleMaps = lazy(() => import("./pages/soluzioni/GoogleMaps"));
const SitoVetrina = lazy(() => import("./pages/soluzioni/SitoVetrina"));
const Ecommerce = lazy(() => import("./pages/soluzioni/Ecommerce"));
const PrenotazioniTavoli = lazy(() => import("./pages/soluzioni/PrenotazioniTavoli"));
const MarketingCompleto = lazy(() => import("./pages/soluzioni/MarketingCompleto"));
const EmailSmsFidelizzazione = lazy(() => import("./pages/soluzioni/EmailSmsFidelizzazione"));

// Blog pages - lazy
const Insights = lazy(() => import("./pages/Insights"));
const Article = lazy(() => import("./pages/Article"));

// Contact page - lazy
const Contatti = lazy(() => import("./pages/Contatti"));

// Local landing pages - lazy
const Campobasso = lazy(() => import("./pages/local/Campobasso"));
const Pescara = lazy(() => import("./pages/local/Pescara"));
const Benevento = lazy(() => import("./pages/local/Benevento"));
const Boiano = lazy(() => import("./pages/local/Boiano"));
const Termoli = lazy(() => import("./pages/local/Termoli"));

// Divisioni + Promozioni - lazy
const MarketingAgency = lazy(() => import("./pages/MarketingAgency"));
const DigitalSolutions = lazy(() => import("./pages/DigitalSolutions"));
const Promozioni = lazy(() => import("./pages/Promozioni"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/servizi" element={<Servizi />} />
              <Route path="/servizi/b2b" element={<ServiziB2B />} />
              <Route path="/servizi/b2c" element={<ServiziB2C />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookie" element={<Cookie />} />
              
              {/* B2B Solution pages */}
              <Route path="/soluzioni/automazioni-ai" element={<AutomazioniAI />} />
              <Route path="/soluzioni/crm" element={<CRM />} />
              <Route path="/soluzioni/email-marketing" element={<EmailMarketing />} />
              <Route path="/soluzioni/database-leads" element={<DatabaseLeads />} />
              <Route path="/soluzioni/ads-b2b" element={<AdsB2B />} />
              <Route path="/soluzioni/seo" element={<SEO />} />
              <Route path="/soluzioni/sviluppo-web" element={<SviluppoWeb />} />
              <Route path="/soluzioni/consulenza" element={<Consulenza />} />
              <Route path="/soluzioni/social-b2b" element={<SocialB2B />} />
              <Route path="/soluzioni/gestionali" element={<Gestionali />} />
              <Route path="/soluzioni/integrazioni" element={<Integrazioni />} />
              <Route path="/soluzioni/software-custom" element={<SoftwareCustom />} />
              
              {/* B2C Solution pages */}
              <Route path="/soluzioni/assistente-whatsapp" element={<AssistenteWhatsApp />} />
              <Route path="/soluzioni/segretaria-ai" element={<SegretariaAI />} />
              <Route path="/soluzioni/prenotazioni-online" element={<PrenotazioniOnline />} />
              <Route path="/soluzioni/social-media-locale" element={<SocialMediaLocale />} />
              <Route path="/soluzioni/ads-locale" element={<AdsLocale />} />
              <Route path="/soluzioni/google-maps" element={<GoogleMaps />} />
              <Route path="/soluzioni/sito-vetrina" element={<SitoVetrina />} />
              <Route path="/soluzioni/ecommerce" element={<Ecommerce />} />
              <Route path="/soluzioni/prenotazioni-tavoli" element={<PrenotazioniTavoli />} />
              <Route path="/soluzioni/marketing-completo" element={<MarketingCompleto />} />
              <Route path="/soluzioni/email-sms-fidelizzazione" element={<EmailSmsFidelizzazione />} />
              
              {/* Blog/Insights pages */}
              <Route path="/insights" element={<Insights />} />
              <Route path="/insights/:slug" element={<Article />} />
              
              {/* Contact page */}
              <Route path="/contatti" element={<Contatti />} />
              
              {/* Local landing pages */}
              <Route path="/marketing-digitale-campobasso" element={<Campobasso />} />
              <Route path="/agenzia-marketing-pescara" element={<Pescara />} />
              <Route path="/marketing-digitale-benevento" element={<Benevento />} />
              <Route path="/marketing-digitale-boiano" element={<Boiano />} />
              <Route path="/agenzia-marketing-termoli" element={<Termoli />} />

              {/* Divisioni + Promozioni */}
              <Route path="/marketing-agency" element={<MarketingAgency />} />
              <Route path="/digital-solutions" element={<DigitalSolutions />} />
              <Route path="/promozioni" element={<Promozioni />} />
              <Route path="/promo" element={<Promozioni />} />

              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;